/**
 * English locale strings.
 * Add new locales as sibling files (es.ts, fr.ts, ar.ts ...) with the same shape.
 */
export const en = {
  home: {
    tagline: "Slow living, Southern charm, and bohemian style — curated fashion for the free-spirited soul.",
    footerTagline: "Slow living, Southern charm, and bohemian style. Curated fashion for the free-spirited soul.",
  },
  promoTicker: {
    closeLabel: "Close promotions",
    messages: [
      "✨ Free Shipping on Orders $150+ ✨",
      "🌿 New Boho Styles Added Daily — Shop the Latest",
      "💛 Join the Boho Fam — Get Early Access to New Drops",
      "🌹 Curated Bohemian Fashion — Tops, Dresses, Jewelry & More",
    ],
  },
  shopByCollection: {
    heading: "Shop by Collection",
    tagline: "Six curated boho aesthetics — find the one that feels like you.",
  },
  categoryCard: {
    shopNow: "Shop Now",
    productOne: "product",
    productOther: "products",
  },
  shopByCategory: {
    heading: "Shop by Category",
    tagline: "Find your perfect boho look",
    shopNow: "Shop Now",
    loading: "Loading…",
    stylesSuffix: "styles",
    plusSizeSuffix: "plus size styles",
    tiles: {
      tops: "Tops",
      bottoms: "Bottoms",
      goddess: "Goddess",
      dresses: "Dresses",
      footwear: "Footwear",
      jewelry: "Jewelry",
      "lifestyle-decor": "Lifestyle & Decor",
    },
  },
  megaMenu: {
    trigger: "Collections",
    ariaLabel: "Collections menu",
    viewAll: "View All →",
    viewAllCollections: "View All Collections",
    apparelHeading: "Apparel",
    plusLabel: "Plus",
    /**
     * Localized labels keyed by TaxonomyNode `id` from src/config/taxonomy.ts.
     * Falls back to node.label when an id is missing.
     */
    taxonomy: {
      apparel: "Apparel",
      goddess: "Goddess",
      lifestyle: "Lifestyle",
      sandals: "Sandals",
      boots: "Boots",
      heels: "Heels",
      jewelry: "Jewelry",
      accessories: "Accessories",
      bags: "Bags",
      dresses: "Dresses",
      "one-pieces": "One-Pieces",
      loungewear: "Loungewear",
      footwear: "Footwear",
      "lifestyle-decor": "Lifestyle & Decor",
      tops: "Tops",
      bottoms: "Bottoms",
      "outfit-sets": "Outfit Sets",
      necklaces: "Necklaces",
      earrings: "Earrings",
      bracelets: "Bracelets",
      rings: "Rings",
      "bag-charms": "Bag Charms",
      belts: "Belts",
      hats: "Hats",
      "hair-accessories": "Hair Accessories",
      sunglasses: "Sunglasses",
      "crossbody-bags": "Crossbody Bags",
      handbags: "Handbags",
      "shoulder-bags": "Shoulder Bags",
      "tote-bags": "Tote Bags",
      "weekender-bags": "Weekender Bags",
      "maxi-dresses": "Maxi Dresses",
      "midi-dresses": "Midi Dresses",
      "mini-dresses": "Mini Dresses",
      jumpsuits: "Jumpsuits",
      overalls: "Overalls",
      rompers: "Rompers",
      "lounge-set": "Lounge Set",
      pajamas: "Pajamas",
      "pajamas-set": "Pajamas Set",
      "hot-chocolate-design": "Hot Chocolate Design",
      "colorstep-fashion": "Colorstep Fashion co.Ltd",
      "area-rugs": "Area Rugs",
      "accent-furniture": "Accent Furniture",
      "bedding-sets": "Bedding Sets",
      "table-decor": "Table Decor",
      "wall-mirrors": "Wall Mirrors",
      "throw-blankets": "Throw Blankets",
      "wall-hangings": "Wall Hangings",
    },
  },
  bohoCollections: {
    "boho-western": {
      title: "Boho: Western",
      tagline: "Fringe, suede & turquoise spirit",
      metaDescription:
        "Fringe, suede, turquoise and prairie spirit — western-tinged boho style.",
    },
    "boho-cottagecore": {
      title: "Boho: Cottagecore",
      tagline: "Florals, eyelet & prairie romance",
      metaDescription:
        "Florals, eyelet and prairie romance — cottagecore boho pieces.",
    },
    "boho-coastal": {
      title: "Boho: Coastal",
      tagline: "Kaftans, raffia & shoreline ease",
      metaDescription:
        "Kaftans, raffia, shells and resort ease — coastal boho style.",
    },
    "boho-festival": {
      title: "Boho: Festival",
      tagline: "Sequins, metallics & statement fringe",
      metaDescription:
        "Sequins, metallics and statement fringe — festival-ready boho.",
    },
    "boho-earthy": {
      title: "Boho: Earthy",
      tagline: "Terracotta, clay & raw artisan textures",
      metaDescription:
        "Terracotta, clay and raw artisan textures — earthy grounded boho.",
    },
    "boho-modern": {
      title: "Boho: Modern",
      tagline: "Minimal, structured quiet luxury",
      metaDescription:
        "Minimal, structured, monochrome — quiet luxury boho.",
    },
  },
} as const;

type BohoCollectionStrings = {
  title: string;
  tagline: string;
  metaDescription: string;
};

export type BohoCollectionHandle = keyof typeof en.bohoCollections;

export type CategoryTileKey =
  | "tops"
  | "bottoms"
  | "goddess"
  | "dresses"
  | "footwear"
  | "jewelry"
  | "lifestyle-decor";

export type LocaleStrings = {
  home: {
    tagline: string;
    footerTagline: string;
  };
  promoTicker: {
    closeLabel: string;
    messages: readonly string[];
  };
  shopByCollection: {
    heading: string;
    tagline: string;
  };
  categoryCard: {
    shopNow: string;
    productOne: string;
    productOther: string;
  };
  shopByCategory: {
    heading: string;
    tagline: string;
    shopNow: string;
    loading: string;
    stylesSuffix: string;
    plusSizeSuffix: string;
    tiles: Record<CategoryTileKey, string>;
  };
  megaMenu: {
    trigger: string;
    ariaLabel: string;
    viewAll: string;
    viewAllCollections: string;
    apparelHeading: string;
    plusLabel: string;
    taxonomy: Record<string, string>;
  };
  bohoCollections: Record<BohoCollectionHandle, BohoCollectionStrings>;
};
